/**
 * registrationApi.ts
 * RTK Query endpoints for registration period management.
 * Replaces direct supabase.from() / .upsert() calls in RegistrationWindowModal.
 */
import { rootApi } from "../../../../lib/api/rootApi";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RegistrationPeriodRow {
  id: string;
  title: string;
  subtitle?: string | null;
  instructions?: string | null;
  internal_notes?: string | null;
  status: string;
  opened_at?: string | null;
  closed_at?: string | null;
  course_start_date?: string | null;
  semester_id?: string | null;
  max_wishes_total?: number | null;
  max_wishes_per_day?: number | null;
}

export interface RegistrationInitBundle {
  periods: RegistrationPeriodRow[];
  semesters: any[];
  classes: any[];
  scheduledRows: any[];
}

export interface RegistrationWindowRow {
  course_id: string;
  opens_at?: string | null;
  closes_at?: string | null;
}

// ---------------------------------------------------------------------------
// Injected endpoints
// ---------------------------------------------------------------------------

export const registrationApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    // ── Initial data bundle (4 parallel selects) ────────────────────
    getRegistrationInitBundle: builder.query<
      RegistrationInitBundle,
      { schoolId: string }
    >({
      async queryFn(arg, _api, _extraOptions, baseQuery) {
        const [periodsRes, semsRes, classesRes, schedulesRes] = await Promise.all([
          baseQuery({
            type: "select",
            table: "registration_periods",
            params: {
              select: "id,title,subtitle,instructions,internal_notes,status,opened_at,closed_at,course_start_date,semester_id,max_wishes_total,max_wishes_per_day",
              school_id: arg.schoolId,
              order: "created_at.desc",
            },
          }),
          baseQuery({
            type: "select",
            table: "structure_school_semesters",
            params: {
              select: "id,name,start_date,end_date,is_active",
              school_id: arg.schoolId,
              order: "start_date",
            },
          }),
          baseQuery({
            type: "select",
            table: "structure_classes",
            params: {
              select: "id,name,grade_level",
              school_id: arg.schoolId,
              order: "name",
            },
          }),
          baseQuery({
            type: "select",
            table: "course_schedules",
            params: {
              select: "course_id,day_id,teacher_ids,primary_teacher_id",
              school_id: arg.schoolId,
              is_archived: false,
            },
          }),
        ]);

        if (periodsRes.error) return { error: periodsRes.error };
        if (semsRes.error) return { error: semsRes.error };
        if (classesRes.error) return { error: classesRes.error };
        if (schedulesRes.error) return { error: schedulesRes.error };

        return {
          data: {
            periods: (periodsRes.data || []) as RegistrationPeriodRow[],
            semesters: (semsRes.data || []) as any[],
            classes: (classesRes.data || []) as any[],
            scheduledRows: (schedulesRes.data || []) as any[],
          },
        };
      },
      providesTags: ["Courses", "Settings"],
    }),

    // ── Load teacher abbreviations by IDs ───────────────────────────
    getTeacherAbbreviations: builder.query<
      Record<string, string>,
      { teacherIds: string[] }
    >({
      async queryFn(arg, _api, _extraOptions, baseQuery) {
        if (arg.teacherIds.length === 0) return { data: {} };

        const [staffRes, extRes] = await Promise.all([
          baseQuery({
            type: "select",
            table: "vw_react_staff_management",
            params: { select: "id, kurzung", id: { in: arg.teacherIds } },
          }),
          baseQuery({
            type: "select",
            table: "vw_react_staff_externals",
            params: { select: "id, kurzung", id: { in: arg.teacherIds } },
          }),
        ]);

        const map: Record<string, string> = {};
        const staffData = (staffRes.error ? [] : (staffRes.data || [])) as any[];
        const extData = (extRes.error ? [] : (extRes.data || [])) as any[];
        staffData.forEach((s: any) => {
          if (s?.id) map[String(s.id)] = s.kurzung || "";
        });
        extData.forEach((s: any) => {
          if (s?.id && !map[String(s.id)]) map[String(s.id)] = s.kurzung || "";
        });
        return { data: map };
      },
      providesTags: ["Staff"],
    }),

    // ── Load windows for a period ───────────────────────────────────
    getRegistrationWindows: builder.query<
      RegistrationWindowRow[],
      { registrationPeriodId: string }
    >({
      query: (params) => ({
        type: "select",
        table: "course_registration_windows",
        params: {
          select: "course_id,opens_at,closes_at",
          registration_period_id: params.registrationPeriodId,
        },
      }),
      providesTags: ["Courses"],
    }),

    // ── Upsert registration period ──────────────────────────────────
    upsertRegistrationPeriod: builder.mutation<any, {
      row: any;
      schoolId: string;
    }>({
      async queryFn(arg, _api, _extraOptions, baseQuery) {
        const upsertRes = await baseQuery({
          type: "upsert",
          table: "registration_periods",
          params: {
            data: arg.row,
            onConflict: "id",
          },
        });
        if (upsertRes.error) return { error: upsertRes.error };

        // Refresh the periods list
        const refreshRes = await baseQuery({
          type: "select",
          table: "registration_periods",
          params: {
            select: "id,title,subtitle,instructions,internal_notes,status,opened_at,closed_at,course_start_date,semester_id,max_wishes_total,max_wishes_per_day",
            school_id: arg.schoolId,
            order: "created_at.desc",
          },
        });
        if (refreshRes.error) return { error: refreshRes.error };
        return { data: refreshRes.data };
      },
    }),

    // ── Save registration windows (delete removed + upsert selected) ─
    saveRegistrationWindows: builder.mutation<any, {
      registrationPeriodId: string;
      schoolId: string;
      payload: any[];
      toDelete: string[];
    }>({
      async queryFn(arg, _api, _extraOptions, baseQuery) {
        // Delete removed windows
        if (arg.toDelete.length > 0) {
          const delRes = await baseQuery({
            type: "delete",
            table: "course_registration_windows",
            match: {
              registration_period_id: arg.registrationPeriodId,
              course_id: { in: arg.toDelete },
            },
          });
          if (delRes.error) return { error: delRes.error };
        }

        // Upsert selected windows
        if (arg.payload.length > 0) {
          const upsertRes = await baseQuery({
            type: "upsert",
            table: "course_registration_windows",
            params: {
              data: arg.payload,
              onConflict: "id",
            },
          });
          if (upsertRes.error) return { error: upsertRes.error };
        }

        return { data: { success: true } };
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(rootApi.util.invalidateTags(["Courses"]));
        } catch { /* noop */ }
      },
    }),
  }),
});

export const {
  useGetRegistrationInitBundleQuery,
  useLazyGetRegistrationInitBundleQuery,
  useGetTeacherAbbreviationsQuery,
  useLazyGetRegistrationWindowsQuery,
  useUpsertRegistrationPeriodMutation,
  useSaveRegistrationWindowsMutation,
} = registrationApi;
